import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Scan, Copy, Check, Key, Zap, FileText, Globe, ArrowRight, Crown } from 'lucide-react';
import PageHeader from '../components/PageHeader';

const API_BASE = 'https://veritas-scan-production.up.railway.app';

function CodeBlock({ code, language = 'bash' }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="relative bg-zinc-950 border border-zinc-800 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-800 bg-zinc-900">
        <span className="font-mono text-xs text-zinc-500">{language}</span>
        <button onClick={copy} className="flex items-center gap-1 text-zinc-500 hover:text-white transition-colors font-mono text-xs">
          {copied ? <><Check className="w-3 h-3 text-emerald-400" /> Copiado</> : <><Copy className="w-3 h-3" /> Copiar</>}
        </button>
      </div>
      <pre className="p-4 text-sm font-mono text-zinc-300 overflow-x-auto whitespace-pre">{code}</pre>
    </div>
  );
}

export default function ApiDocs({ onBack, onNavigateAuth, user }) {
  const [activeSection, setActiveSection] = useState('autenticacion');

  // Track active section on scroll
  useEffect(() => {
    const sectionIds = ['autenticacion', 'rate-limits', 'errores', 'endpoint-analizar', 'endpoint-resultado'];
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-20% 0px -60% 0px', threshold: 0 }
    );
    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(id);
    }
  };

  const introItems = [
    { id: 'autenticacion', label: 'Autenticación' },
    { id: 'rate-limits', label: 'Rate limits' },
    { id: 'errores', label: 'Errores' },
  ];

  const endpoints = [
    { id: 'endpoint-analizar', label: 'Analizar', method: 'POST' },
    { id: 'endpoint-resultado', label: 'Resultado', method: 'GET' },
  ];

  const sidebarItemClass = (id) =>
    `text-left text-xs sm:text-sm py-1.5 px-3 lg:px-2 rounded-lg border whitespace-nowrap transition-colors cursor-pointer ${
      activeSection === id
        ? 'text-blue-400 border-blue-500/30 bg-blue-500/10'
        : 'text-zinc-400 hover:text-white border-zinc-800 bg-zinc-900 lg:bg-transparent lg:border-transparent hover:bg-zinc-800/50'
    }`;

  return (
    <div className="min-h-screen bg-zinc-950 relative">
      <div className="scanlines" />
      <div className="fixed inset-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 50% 0%, rgba(59, 130, 246, 0.08), transparent 60%)' }} />

      {/* Header */}
      <div className="relative z-40">
        <PageHeader onBack={onBack} />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-8">

          {/* Sidebar — sticky on desktop, horizontal scroll on mobile */}
          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-20">
              {/* Mobile: horizontal scroll */}
              <div className="flex lg:hidden gap-2 overflow-x-auto pb-2">
                {introItems.map(item => (
                  <button key={item.id} onClick={() => scrollTo(item.id)} className={sidebarItemClass(item.id)}>
                    {item.label}
                  </button>
                ))}
                {endpoints.map(ep => (
                  <button key={ep.id} onClick={() => scrollTo(ep.id)}
                    className={`flex items-center gap-1.5 text-xs py-1.5 px-3 rounded-lg border whitespace-nowrap transition-colors cursor-pointer ${
                      activeSection === ep.id
                        ? 'text-blue-400 border-blue-500/30 bg-blue-500/10'
                        : 'text-zinc-400 hover:text-white border-zinc-800 bg-zinc-900'
                    }`}>
                    <span className={`font-mono text-xs px-1 py-0.5 rounded ${ep.method === 'POST' ? 'bg-blue-500/20 text-blue-400' : 'bg-emerald-500/20 text-emerald-400'}`}>{ep.method}</span>
                    {ep.label}
                  </button>
                ))}
              </div>

              {/* Desktop: vertical sidebar */}
              <div className="hidden lg:flex flex-col gap-5">
                <div>
                  <p className="font-mono text-xs text-zinc-500 uppercase tracking-widest mb-2">Introducción</p>
                  <div className="flex flex-col gap-0.5">
                    {introItems.map(item => (
                      <button key={item.id} onClick={() => scrollTo(item.id)} className={sidebarItemClass(item.id)}>
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="font-mono text-xs text-zinc-500 uppercase tracking-widest mb-2">Endpoints</p>
                  <div className="flex flex-col gap-0.5">
                    {endpoints.map(ep => (
                      <button key={ep.id} onClick={() => scrollTo(ep.id)}
                        className={`flex items-center gap-2 text-sm py-1.5 px-2 rounded-lg transition-colors cursor-pointer ${
                          activeSection === ep.id
                            ? 'text-blue-400 bg-blue-500/10'
                            : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
                        }`}>
                        <span className={`font-mono text-xs px-1.5 py-0.5 rounded ${ep.method === 'POST' ? 'bg-blue-500/20 text-blue-400' : 'bg-emerald-500/20 text-emerald-400'}`}>{ep.method}</span>
                        {ep.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </aside>

          {/* Main content */}
          <main className="lg:col-span-3 space-y-10 sm:space-y-12">

            {/* Hero */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
              <p className="font-mono text-xs tracking-widest text-blue-500 uppercase">Documentación</p>
              <h1 className="font-heading text-3xl sm:text-4xl font-black">API de Veritas Scan</h1>
              <p className="text-zinc-400 text-base sm:text-lg max-w-xl">Integra la detección de contenido IA en tus aplicaciones con nuestra API REST. Disponible para usuarios Pro y Business.</p>
              {!user && (
                <button onClick={onNavigateAuth} className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white font-mono text-sm uppercase tracking-wider rounded-lg transition-all">
                  Obtener acceso <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </motion.div>

            {/* Base URL */}
            <section className="space-y-3">
              <h2 className="font-heading text-xl font-bold flex items-center gap-2"><Globe className="w-5 h-5 text-blue-400" /> URL base</h2>
              <CodeBlock code={API_BASE} language="url" />
            </section>

            {/* Authentication */}
            <section id="autenticacion" className="space-y-4 scroll-mt-24">
              <h2 className="font-heading text-xl font-bold flex items-center gap-2"><Key className="w-5 h-5 text-yellow-400" /> Autenticación</h2>
              <p className="text-zinc-400 text-sm leading-relaxed">
                Todas las peticiones a la API requieren autenticación mediante tu <strong className="text-white">API key</strong>, que debes incluir en el header <code className="text-blue-300 bg-zinc-800 px-1.5 py-0.5 rounded text-xs">X-API-Key</code> de cada petición. El análisis usa <strong className="text-white">Gemini 2.5 Flash</strong> con detección forense de imágenes, vídeos y textos.
              </p>
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 flex items-start gap-3">
                <Crown className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-yellow-400 text-sm font-mono font-bold mb-1">Requiere plan Pro o Business</p>
                  <p className="text-zinc-400 text-xs">Genera tu API key desde tu perfil de usuario en <strong className="text-white">veritas-scan.com</strong>. Las keys tienen el formato <code className="text-blue-300">vs_xxxxxxxxxxxxxxxx</code>. Soporta imágenes (JPG, PNG, GIF, WebP), vídeos (MP4, MOV, AVI, WebM, MKV), documentos (PDF, DOCX, TXT) y URLs.</p>
                </div>
              </div>
              <CodeBlock language="bash" code={`# Incluye tu API key en el header de cada petición
curl -H "X-API-Key: vs_tu_api_key_aqui" \\
  ${API_BASE}/api/v1/analyze`} />
            </section>

            {/* Rate limits */}
            <section id="rate-limits" className="space-y-4 scroll-mt-24">
              <h2 className="font-heading text-xl font-bold flex items-center gap-2"><Zap className="w-5 h-5 text-purple-400" /> Rate limits</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm font-mono">
                  <thead>
                    <tr className="border-b border-zinc-800 text-zinc-500 text-xs uppercase tracking-wider">
                      <th className="text-left pb-3 pr-6">Plan</th>
                      <th className="text-left pb-3 pr-6">Llamadas/día</th>
                      <th className="text-left pb-3">Tamaño máx. archivo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800/50">
                    {[
                      { plan: 'Pro', calls: '500', size: '10 MB' },
                      { plan: 'Business', calls: '5.000', size: '50 MB' },
                      { plan: 'Enterprise', calls: 'Personalizado', size: 'Personalizado' },
                    ].map(row => (
                      <tr key={row.plan}>
                        <td className="py-3 pr-6 text-zinc-300">{row.plan}</td>
                        <td className="py-3 pr-6 text-emerald-400">{row.calls}</td>
                        <td className="py-3 text-zinc-400">{row.size}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Endpoints */}
            <section className="space-y-8">
              <h2 className="font-heading text-xl font-bold flex items-center gap-2"><FileText className="w-5 h-5 text-emerald-400" /> Endpoints</h2>

              {/* POST analyze */}
              <div id="endpoint-analizar" className="space-y-4 scroll-mt-24">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs px-2 py-1 bg-blue-500/20 text-blue-400 rounded">POST</span>
                  <code className="font-mono text-sm text-zinc-300">/api/v1/analyze</code>
                </div>
                <p className="text-zinc-400 text-sm">Envía un archivo o URL para analizarlo. El análisis es asíncrono — recibirás un <code className="text-blue-300 bg-zinc-800 px-1 rounded text-xs">job_id</code> para consultar el resultado. Soporta imágenes, vídeos, PDF, DOCX, TXT y URLs. Los vídeos pueden tardar hasta 2 minutos en procesarse.</p>

                <div className="space-y-2">
                  <p className="font-mono text-xs text-zinc-500 uppercase tracking-wider">Opción 1 — Imagen o vídeo</p>
                  <CodeBlock language="bash" code={`# Imagen
curl -X POST ${API_BASE}/api/v1/analyze \\
  -H "X-API-Key: vs_tu_api_key" \\
  -F "file=@/ruta/a/imagen.jpg"

# Vídeo (MP4, MOV, AVI, WebM, MKV)
curl -X POST ${API_BASE}/api/v1/analyze \\
  -H "X-API-Key: vs_tu_api_key" \\
  -F "file=@/ruta/a/video.mp4"`} />
                </div>

                <div className="space-y-2">
                  <p className="font-mono text-xs text-zinc-500 uppercase tracking-wider">Opción 2 — URL</p>
                  <CodeBlock language="bash" code={`curl -X POST ${API_BASE}/api/v1/analyze \\
  -H "X-API-Key: vs_tu_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{"url": "https://ejemplo.com/articulo"}'`} />
                </div>

                <div className="space-y-2">
                  <p className="font-mono text-xs text-zinc-500 uppercase tracking-wider">Respuesta</p>
                  <CodeBlock language="json" code={`{
  "job_id": "3f8a1c2d-...",
  "status": "pending",
  "docs": "https://veritas-scan.com/api-docs"
}`} />
                </div>

                <div className="space-y-2">
                  <p className="font-mono text-xs text-zinc-500 uppercase tracking-wider">Ejemplo en Python</p>
                  <CodeBlock language="python" code={`import requests, time

API_KEY = "vs_tu_api_key"
BASE = "${API_BASE}"

# 1. Enviar archivo
with open("imagen.jpg", "rb") as f:
    r = requests.post(
        f"{BASE}/api/v1/analyze",
        headers={"X-API-Key": API_KEY},
        files={"file": f}
    )
job_id = r.json()["job_id"]

# 2. Esperar resultado (polling)
while True:
    r = requests.get(
        f"{BASE}/api/v1/result/{job_id}",
        headers={"X-API-Key": API_KEY}
    )
    data = r.json()
    if data["status"] == "completed":
        print(data["result"])
        break
    elif data["status"] == "failed":
        print("Error:", data["error"])
        break
    time.sleep(2)`} />
                </div>
              </div>

              {/* GET result */}
              <div id="endpoint-resultado" className="space-y-4 border-t border-zinc-800 pt-8 scroll-mt-24">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded">GET</span>
                  <code className="font-mono text-sm text-zinc-300">/api/v1/result/{'{job_id}'}</code>
                </div>
                <p className="text-zinc-400 text-sm">Consulta el estado y resultado de un análisis. Haz polling cada 2 segundos hasta que <code className="text-blue-300 bg-zinc-800 px-1 rounded text-xs">status</code> sea <code className="text-emerald-300 bg-zinc-800 px-1 rounded text-xs">completed</code> o <code className="text-red-300 bg-zinc-800 px-1 rounded text-xs">failed</code>.</p>

                <CodeBlock language="bash" code={`curl ${API_BASE}/api/v1/result/3f8a1c2d-... \\
  -H "X-API-Key: vs_tu_api_key"`} />

                <div className="space-y-2">
                  <p className="font-mono text-xs text-zinc-500 uppercase tracking-wider">Respuesta (completado)</p>
                  <CodeBlock language="json" code={`{
  "job_id": "3f8a1c2d-...",
  "status": "completed",
  "result": {
    "id": "scan_abc123",
    "verdict": "ai",
    "confidence": 94,
    "explanation": "El texto presenta patrones...",
    "details": [
      "Estructura repetitiva detectada",
      "Ausencia de variaciones de estilo"
    ],
    "file_name": "imagen.jpg",
    "file_type": "image",  // image | video | pdf | text | docx
    "created_at": "2026-03-01T10:30:00Z"
  }
}`} />
                </div>
              </div>
            </section>

            {/* Error codes */}
            <section id="errores" className="space-y-4 scroll-mt-24">
              <h2 className="font-heading text-xl font-bold">Códigos de error</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm font-mono">
                  <thead>
                    <tr className="border-b border-zinc-800 text-zinc-500 text-xs uppercase tracking-wider">
                      <th className="text-left pb-3 pr-6">Código</th>
                      <th className="text-left pb-3">Descripción</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800/50">
                    {[
                      { code: '401', desc: 'API key ausente o inválida' },
                      { code: '403', desc: 'Plan insuficiente (se requiere Pro o Business)' },
                      { code: '400', desc: 'Petición malformada o parámetros incorrectos' },
                      { code: '422', desc: 'No se pudo procesar el archivo o URL' },
                      { code: '429', desc: 'Rate limit superado' },
                      { code: '500', desc: 'Error interno del servidor' },
                    ].map(row => (
                      <tr key={row.code}>
                        <td className="py-3 pr-6 text-red-400">{row.code}</td>
                        <td className="py-3 text-zinc-400">{row.desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

          </main>
        </div>
      </div>
    </div>
  );
}
